import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useTeams, useCreateTeam, useTeamMessages } from '../hooks/useTeams';
import { useAgents } from '../hooks/useAgents';
import { useTasks } from '../hooks/useTasks';
import TeamCard from '../components/TeamCard';
import TeamsOnboarding from '../components/TeamsOnboarding';
import CreateTeamModal from '../components/CreateTeamModal';
import type { CreateTeamDto } from '../types/team';

function TeamCardWithMessages({ team }: { team: Parameters<typeof TeamCard>[0]['team'] }) {
  const { data: messages } = useTeamMessages(team.id, 100);
  return <TeamCard team={team} messageCount={messages?.length} />;
}

export default function Teams() {
  const { id: projectId } = useParams<{ id: string }>();
  const { data: teams, isLoading } = useTeams(projectId);
  const { data: agents } = useAgents(projectId);
  const { data: tasks } = useTasks(projectId);
  const createTeam = useCreateTeam(projectId ?? '');
  const [showCreate, setShowCreate] = useState(false);

  const handleCreate = async (data: CreateTeamDto) => {
    await createTeam.mutateAsync(data);
  };

  const taskItems = tasks?.map(t => ({ id: t.id, title: t.title, status: t.status }));

  return (
    <div className="p-8">
      {isLoading ? (
        <div className="py-12 text-center text-sm text-slate-400">Loading teams...</div>
      ) : !teams?.length ? (
        <TeamsOnboarding onCreateTeam={() => setShowCreate(true)} />
      ) : (
        <>
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Teams</h1>
              <p className="mt-1 text-sm text-slate-500">{teams.length} team{teams.length !== 1 ? 's' : ''}</p>
            </div>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <Plus className="h-4 w-4" />
              New Team
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {teams.map(team => (
              <TeamCardWithMessages key={team.id} team={team} />
            ))}
          </div>
        </>
      )}

      <CreateTeamModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreate}
        agents={agents ?? []}
        tasks={taskItems}
        isPending={createTeam.isPending}
      />
    </div>
  );
}
